import React, { useState } from "react";
import Layout from "../../../Layouts/Layout";
import { Link } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import Select from "react-select";
import { SelectStyles } from "../../../styles/ReactSelect";

// Tradesman-side browse. The server only ever returns requests inside the
// tradesman's visibility set (their districts x their trades); the filters
// below narrow within that set and the dropdown options are limited to the
// tradesman's own trades and districts.
const Browse = ({ requests, profile_summary, filters, pagination }) => {
  const [search, setSearch] = useState(filters.q || "");

  const tradeOptions = profile_summary.trades.map((t) => ({
    value: String(t.id),
    label: t.name,
  }));
  const districtOptions = profile_summary.postcodes.map((d) => ({
    value: String(d.id),
    label: d.code,
  }));
  const sortOptions = [
    { value: "newest", label: "Newest first" },
    { value: "oldest", label: "Oldest first" },
    { value: "start_date", label: "Soonest ideal start" },
  ];

  function applyFilters(overrides) {
    const next = {
      trade_id: filters.trade_id,
      postcode_district_id: filters.postcode_district_id,
      q: search.trim() || null,
      sort: filters.sort,
      ...overrides,
    };
    const query = {};
    Object.keys(next).forEach((key) => {
      if (next[key]) query[key] = next[key];
    });
    Inertia.get("/domestic/tradesman_requests", query, {
      preserveState: true,
      preserveScroll: true,
    });
  }

  function submitSearch(e) {
    e.preventDefault();
    applyFilters({});
  }

  const hasActiveFilters =
    filters.trade_id || filters.postcode_district_id || filters.q;

  return (
    <Layout title="Domestic Requests">
      <div className="mt-4 max-w-3xl">
        <div className="flex items-center justify-between rounded-md bg-gray-50 border border-gray-200 px-4 py-3">
          <p className="text-sm text-gray-600">
            Showing open requests in{" "}
            <span className="font-medium text-gray-900">
              {profile_summary.postcodes.map((d) => d.code).join(", ")}
            </span>{" "}
            for{" "}
            <span className="font-medium text-gray-900">
              {profile_summary.trades.map((t) => t.name).join(", ")}
            </span>
          </p>
          <Link
            href="/domestic/tradesman_profile/edit"
            className="ml-4 flex-shrink-0 text-sm font-medium text-orange-600 hover:text-orange-500"
          >
            Edit profile
          </Link>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Trade
            </label>
            <Select
              styles={SelectStyles}
              options={tradeOptions}
              isClearable
              placeholder="All my trades"
              value={
                tradeOptions.find((o) => o.value === filters.trade_id) || null
              }
              onChange={(selected) =>
                applyFilters({ trade_id: selected ? selected.value : null })
              }
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Postcode district
            </label>
            <Select
              styles={SelectStyles}
              options={districtOptions}
              isClearable
              placeholder="All my districts"
              value={
                districtOptions.find(
                  (o) => o.value === filters.postcode_district_id
                ) || null
              }
              onChange={(selected) =>
                applyFilters({
                  postcode_district_id: selected ? selected.value : null,
                })
              }
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Sort by
            </label>
            <Select
              styles={SelectStyles}
              options={sortOptions}
              value={sortOptions.find((o) => o.value === filters.sort)}
              onChange={(selected) => applyFilters({ sort: selected.value })}
            />
          </div>
        </div>

        <form onSubmit={submitSearch} className="mt-3 flex gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search request descriptions…"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-orange-500 sm:text-sm"
          />
          <button
            type="submit"
            className="rounded-md bg-orange-600 py-2 px-4 text-sm font-medium text-white hover:bg-orange-700"
          >
            Search
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                Inertia.get("/domestic/tradesman_requests");
              }}
              className="rounded-md border border-gray-300 py-2 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </form>

        <p className="mt-3 text-xs text-gray-500">
          {pagination.total}{" "}
          {pagination.total === 1 ? "request" : "requests"} found
        </p>

        {requests.length === 0 ? (
          <div className="mt-3 text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-sm text-gray-500">
              {hasActiveFilters
                ? "No requests match these filters."
                : "No open requests in your postcode districts right now. Add more districts from your profile to widen your reach."}
            </p>
          </div>
        ) : (
          <ul className="mt-3 divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
            {requests.map((r) => (
              <li key={r.id}>
                <Link
                  href={`/domestic/tradesman_requests/${r.id}`}
                  className="block px-4 py-4 hover:bg-gray-50"
                >
                  <div className="flex items-center justify-between">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {r.trades.join(", ")} — {r.postcode_district_code}
                    </p>
                    {r.responded && (
                      <span className="inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold leading-5 text-blue-800">
                        responded
                      </span>
                    )}
                  </div>
                  <p className="mt-1 truncate text-sm text-gray-500">
                    {r.description}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Posted {r.created_at}
                    {r.ideal_start_date
                      ? ` · Ideal start ${r.ideal_start_date}`
                      : ""}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {pagination.total_pages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => applyFilters({ page: pagination.page - 1 })}
              className="rounded-md border border-gray-300 py-1.5 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              &larr; Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <button
              type="button"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => applyFilters({ page: pagination.page + 1 })}
              className="rounded-md border border-gray-300 py-1.5 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              Next &rarr;
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Browse;
